const DEVICE_ID_KEY = "flowchest_device_id";
const GEO_CACHE_KEY = "flowchest_last_geo";

export const ADVANCED_DENIAL_CODES = [
  "NETWORK_BLOCKED",
  "GEO_BLOCKED",
  "GEO_LOCATION_REQUIRED",
  "DEVICE_NOT_ALLOWED",
  "COMPANY_EMAIL_REQUIRED",
  "PLAN_UPGRADE_REQUIRED",
  "ACCESS_METHODS_FAILED",
  "NOT_IN_ALLOWLIST",
  "AUTH_REQUIRED",
  "ORG_MEMBERS_ONLY",
] as const;

export const PENDING_FLOW_ACCESS_KEY = "pendingFlowAccessRequest";

export type PendingFlowAccessRequest = {
  flowId: string;
  denialCode?: string;
  denialMessage?: string;
  isRestricted?: boolean;
  isPrivate?: boolean;
  isAdvanced?: boolean;
};

export function savePendingFlowAccessRequest(
  pending: PendingFlowAccessRequest
): void {
  try {
    localStorage.setItem(PENDING_FLOW_ACCESS_KEY, JSON.stringify(pending));
  } catch {
    // ignore storage failures
  }
}

export function peekPendingFlowAccessRequest(
  flowId?: string
): PendingFlowAccessRequest | null {
  try {
    const raw = localStorage.getItem(PENDING_FLOW_ACCESS_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as PendingFlowAccessRequest;
    if (!parsed?.flowId) {
      return null;
    }
    if (flowId && parsed.flowId !== flowId) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearPendingFlowAccessRequest(): void {
  try {
    localStorage.removeItem(PENDING_FLOW_ACCESS_KEY);
  } catch {
    // ignore storage failures
  }
}

/** Stable per-browser device id used for Growth/Enterprise device gates. */
export function getOrCreateDeviceId(): string {
  try {
    const existing = localStorage.getItem(DEVICE_ID_KEY);
    if (existing && existing.trim()) {
      return existing.trim();
    }
    const id =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `dev-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    return `dev-session-${Date.now()}`;
  }
}

const GEO_CACHE_TTL_MS = 5 * 60 * 1000;

export type GeoPosition = {
  latitude: number;
  longitude: number;
  accuracy?: number;
};

export function cacheGeoPosition(coords: GeoPosition): void {
  const payload = JSON.stringify({
    latitude: coords.latitude,
    longitude: coords.longitude,
    accuracy: coords.accuracy,
    at: Date.now(),
  });
  try {
    localStorage.setItem(GEO_CACHE_KEY, payload);
  } catch {
    // ignore storage failures
  }
  try {
    sessionStorage.setItem(GEO_CACHE_KEY, payload);
  } catch {
    // ignore storage failures
  }
}

export function getCachedGeoPosition(): GeoPosition | null {
  const raw =
    (() => {
      try {
        return localStorage.getItem(GEO_CACHE_KEY);
      } catch {
        return null;
      }
    })() ||
    (() => {
      try {
        return sessionStorage.getItem(GEO_CACHE_KEY);
      } catch {
        return null;
      }
    })();
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.latitude !== "number" ||
      typeof parsed?.longitude !== "number"
    ) {
      return null;
    }
    if (typeof parsed.at === "number" && Date.now() - parsed.at > GEO_CACHE_TTL_MS) {
      return null;
    }
    const accuracy =
      typeof parsed.accuracy === "number" && parsed.accuracy > 0
        ? parsed.accuracy
        : undefined;
    return {
      latitude: parsed.latitude,
      longitude: parsed.longitude,
      accuracy,
    };
  } catch {
    return null;
  }
}

export function getDeviceIdHeader(): { [name: string]: string } {
  return { "X-Device-Id": getOrCreateDeviceId() };
}

export function buildAccessContextHeaders(options?: {
  deviceId?: string;
  latitude?: number;
  longitude?: number;
  accuracy?: number;
} | null): { [name: string]: string } {
  const headers: { [name: string]: string } = {
    "X-Device-Id": (options?.deviceId || getOrCreateDeviceId()).trim(),
  };
  const cached = getCachedGeoPosition();
  const lat = options?.latitude ?? cached?.latitude;
  const lng = options?.longitude ?? cached?.longitude;
  const accuracy = options?.accuracy ?? cached?.accuracy;
  if (
    lat != null &&
    lng != null &&
    !Number.isNaN(Number(lat)) &&
    !Number.isNaN(Number(lng))
  ) {
    headers["X-Latitude"] = String(lat);
    headers["X-Longitude"] = String(lng);
    if (accuracy != null && !Number.isNaN(Number(accuracy)) && Number(accuracy) > 0) {
      headers["X-Accuracy"] = String(accuracy);
    }
  }
  return headers;
}

export function getAccessContextHeaders(): { [name: string]: string } {
  return buildAccessContextHeaders();
}

export function isGeoLocationDenialCode(code?: string | null): boolean {
  return code === "GEO_LOCATION_REQUIRED" || code === "GEO_BLOCKED";
}

/** Retry with GPS when location was not sent — including combined-requirement denials. */
export function shouldRetryAccessWithLocation(
  code?: string | null,
  sentCoords?: boolean
): boolean {
  if (sentCoords) {
    return false;
  }
  return isGeoLocationDenialCode(code) || code === "ACCESS_METHODS_FAILED";
}

export function isAdvancedDenialCode(code?: string | null): boolean {
  return !!code && (ADVANCED_DENIAL_CODES as readonly string[]).includes(code);
}

export function accessBlockTypeFromDenial(
  denialCode?: string | null,
  isPrivate?: boolean,
  isRestricted?: boolean
): "advanced" | "private" | "restricted" {
  if (isAdvancedDenialCode(denialCode)) {
    return "advanced";
  }
  if (isPrivate) {
    return "private";
  }
  return isRestricted ? "restricted" : "private";
}

export function denialFromHttpError(error: any): {
  code: string;
  message: string;
} {
  const body = error?.error;
  return {
    code: body?.code || body?.Code || "",
    message: body?.message || body?.Message || "",
  };
}

/** Best-effort GPS for geo-fence checks; resolves nulls if denied/unavailable. */
export function getCurrentPositionOptional(
  timeoutMs = 15000
): Promise<GeoPosition | null> {
  return new Promise((resolve) => {
    const cached = getCachedGeoPosition();
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(cached);
      return;
    }

    let settled = false;
    const finish = (coords: GeoPosition | null) => {
      if (settled) {
        return;
      }
      settled = true;
      if (coords) {
        cacheGeoPosition(coords);
      }
      resolve(coords);
    };

    navigator.geolocation.getCurrentPosition(
      (pos) =>
        finish({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy:
            typeof pos.coords.accuracy === "number" && pos.coords.accuracy > 0
              ? pos.coords.accuracy
              : undefined,
        }),
      () => finish(cached),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 }
    );
  });
}

export function isPdfUrl(url: string, name?: string): boolean {
  const hay = `${url || ""} ${name || ""}`.toLowerCase();
  return (
    hay.includes(".pdf") ||
    hay.includes("application/pdf") ||
    /\/documents\/.+\.pdf(\?|$)/i.test(url || "")
  );
}
